<template>
	<el-dialog title="选择书单" :visible.sync="visible" width="800px" :before-close="handleClose" class="dialog-book-json">
		<div class="dialog-content">
			<!-- 搜索 -->
			<el-input style="display: block" placeholder="请输入关键词搜索" v-model="filterText" clearable prefix-icon="el-icon-search"> </el-input>

			<!-- Tree组件 -->
			<div class="tree-container" v-loading="loading" element-loading-text="加载书单数据中...">
				<el-tree
					style="width: 100%; max-height: calc(100vh - 500px); overflow: auto; display: block; min-height: 300px"
					v-if="treeData.length > 0"
					ref="tree"
					:data="treeData"
					:props="treeProps"
					:show-checkbox="false"
					node-key="id"
					:check-strictly="false"
					:expand-on-click-node="false"
					:filter-node-method="filterNode"
					@node-expand="handleNodeExpand"
				>
					<span class="custom-tree-node" slot-scope="{ node, data }">
						<el-checkbox
							v-if="node.level !== 3"
							style="width: 100%"
							:indeterminate="node.indeterminate"
							:disabled="(data.level === 1 && (!data.children || data.children.length === 0)) || data._placeholder"
							v-model="node.checked"
							@change="val => handleCheckChange(node, data, val)"
						>
							{{ node.label }}
						</el-checkbox>
						<span v-else style="font-size: 13px; color: #606266">{{ node.label }}</span>
					</span>
				</el-tree>
				<div v-else-if="!loading" class="empty-data">暂无书单数据</div>
			</div>
		</div>

		<div slot="footer" class="dialog-footer flexBetween">
			<div class="selection-info">
				<span>已选择书单 {{ selectedCount }} / 3 个（最多选择3个）</span>
				<el-button :style="{ visibility: selectedCount > 0 ? 'visible' : 'hidden' }" type="text" size="mini" @click="clearSelection">清空</el-button>
			</div>
			<div>
				<el-button size="medium" @click="handleClose">取消</el-button>
				<el-button size="medium" type="primary" @click="handleConfirm" :disabled="selectedCount === 0">确认</el-button>
			</div>
		</div>
	</el-dialog>
</template>

<script>
	import { getAIBookList } from '@/api/ai/ai.js';
	import { getCorporateCatalogList } from '@/api/book/corporateCatalog';
	import { mapGetters } from 'vuex';
	export default {
		name: 'DialogBookJson',
		data() {
			return {
				visible: false,
				treeData: [], // 初始为空数组，从API获取数据
				treeProps: {
					children: 'children',
					label: 'name',
					disabled: 'disabled'
				},
				selectedNodes: [],
				loading: false, // 添加loading状态
				selectedCount: 0,
				filterText: '',
				isClearing: false // 防止clearSelection时级联触发check事件
			};
		},

		computed: {
			...mapGetters('User', ['cinfo'])
		},

		watch: {
			filterText(val) {
				this.$refs.tree.filter(val);
				if (!val) {
					// 清空搜索时折叠2级节点，恢复默认展开状态
					this.$nextTick(() => {
						this.treeData.forEach(l1 => {
							(l1.children || []).forEach(l2 => {
								const n2 = this.$refs.tree.getNode(l2.id);
								if (n2 && n2.expanded) n2.collapse();
							});
						});
					});
				}
			}
		},

		methods: {
			async loadAIBookTree() {
				try {
					this.loading = true;
					const res = await getAIBookList();
					if (res.code == 200) {
						// res.data 返回数据格式为对象，包含accountList、corporateList、standardList三个数组
						const { accountList = [], corporateList = [], standardList = [] } = res.data;

						// 过滤掉 docCount === 0 的书单
						const filteredAccountList = accountList.filter(item => item.docCount !== 0);
						const filteredCorporateList = corporateList.filter(item => item.docCount !== 0);
						const filteredStandardList = standardList.filter(item => item.docCount !== 0);

						// 将子列表映射为树节点格式
						const mapChildren = list =>
							(list || []).map(item => ({
								id: item.id,
								name: item.catalogName,
								catalog_type: item.catalogType,
								children: [{ name: '加载中...', _placeholder: true, disabled: true }]
							}));

						// 组装三级树结构：仅包含过滤后非空的列表
						this.treeData = [];
						if (filteredStandardList.length > 0) {
							this.treeData.push({
								level: 1,
								id: 'standard_kb',
								name: '标准法规库',
								children: mapChildren(filteredStandardList),
								disabled: false
							});
						}
						if (filteredCorporateList.length > 0) {
							this.treeData.push({
								level: 1,
								id: 'corporate_kb',
								name: '企业知识库',
								children: mapChildren(filteredCorporateList),
								disabled: false
							});
						}
						if (filteredAccountList.length > 0) {
							this.treeData.push({
								level: 1,
								id: 'personal_kb',
								name: '个人知识库',
								children: mapChildren(filteredAccountList),
								disabled: false
							});
						}

						// 根据企业等级过滤可见知识库
						const level = this.cinfo.corporateLevel;
						this.treeData = this.treeData.filter(item => {
							if (item.id === 'standard_kb') return true;
							if (item.id === 'corporate_kb') return level >= 1;
							if (item.id === 'personal_kb') return level >= 2;
							return true;
						});

					} else {
						this.$message.error('获取AI书单数据失败');
					}
				} catch (error) {
					this.$message.error('获取AI书单数据失败');
					return;
				} finally {
					this.loading = false;

					// 树渲染完成后自动展开 1级 节点（2级手动点击展开时懒加载3级）
					await this.$nextTick();
					if (!this.$refs.tree) return;
					this.treeData.forEach(l1 => {
						const n1 = this.$refs.tree.getNode(l1.id);
						if (n1) n1.expand();
					});
				}
			},

			filterNode(value, data) {
				if (!value) return true;
				if (data._placeholder) return false;
				return data.name.indexOf(value) !== -1;
			},

			// 节点展开时懒加载书籍列表
			async handleNodeExpand(data, node) {
				if (data.children && data.children.length > 0 && !data.children[0]._placeholder) return;
				if (data._loading) return;

				this.$set(data, '_loading', true);
				try {
					const res = await getCorporateCatalogList(data.id, { pageNum: 1, pageSize: 200 });
					if (res.code === 200) {
						const isStandard = node.parent && node.parent.data.id === 'standard_kb';
						if (res.data.totalNum == 0) {
							this.$message.warning(`「${data.name}」书单下暂无文件`);
						}
						const books = (res.data.rows || []).map(item => ({
							id: item.id || item.catalogId,
							name: isStandard ? [item.standardCode, item.standardName].filter(Boolean).join(' ') : item.docName || item.name || '',
							catalog_type: item.catalogType || item.catalog_type
						}));
						this.$set(data, 'children', books);
					}
				} catch (error) {
					console.error('加载书籍列表失败:', error);
				} finally {
					this.$set(data, '_loading', false);
				}
			},

			// 打开弹窗
			async open() {
				this.treeData = [];
				this.filterText = '';
				this.visible = true;
				// 每次打开弹窗时重新获取数据
				this.loadAIBookTree();
				this.$nextTick(() => {
					// 重置选择状态
					this.selectedNodes = [];
					this.selectedCount = 0;
				});
			},

			// 关闭弹窗（取消/X）
			handleClose() {
				this.visible = false;
				this.$emit('cancel');
			},

			// 确认选择
			handleConfirm() {
				if (this.selectedCount === 0) return;
				if (this.selectedCount > 3) {
					this.$message.error('最多选择3个书单');
					return;
				}
				this.$emit('confirm', this.selectedNodes);
				this.visible = false;
			},

			handleCheckChange(node, data, checked) {
				// clearSelection 级联触发时不处理
				if (this.isClearing) return;

				if (checked) {
					// 勾选：一级目录 → 将其下所有二级子节点加入已选+同步勾选；二级节点 → 直接加入自身
					if (node.level === 1 && data.children && data.children.length > 0) {
						// 一级目录：批量添加二级子节点并同步勾选状态
						data.children.forEach(child => {
							if (child._placeholder) return;
							if (!this.selectedNodes.find(item => item.id === child.id)) {
								this.selectedNodes.push({ id: child.id, name: child.name, catalog_type: child.catalog_type });
							}
							// 手动同步子节点 checkbox 勾选（不触发 @change）
							const childNode = this.$refs.tree.getNode(child.id);
							if (childNode) childNode.checked = true;
						});
					} else {
						// 二级节点：单独添加自身（去重）
						if (!this.selectedNodes.find(item => item.id === data.id)) {
							this.selectedNodes.push({ id: data.id, name: data.name, catalog_type: data.catalog_type });
						}
					}
				} else {
					// 取消勾选：一级目录 → 移除其下所有二级子节点+同步取消勾选；二级节点 → 移除自身
					if (node.level === 1 && data.children && data.children.length > 0) {
						const childrenIds = data.children.map(child => child.id);
						this.selectedNodes = this.selectedNodes.filter(item => !childrenIds.includes(item.id));
						// 手动同步子节点取消勾选
						data.children.forEach(child => {
							const childNode = this.$refs.tree.getNode(child.id);
							if (childNode) childNode.checked = false;
						});
					} else {
						this.selectedNodes = this.selectedNodes.filter(item => item.id !== data.id);
					}
				}

				this.selectedCount = this.selectedNodes.length;
			},

			// 清空选择
			clearSelection() {
				this.selectedNodes = [];
				this.selectedCount = 0;
				if (this.$refs.tree) {
					// 设置标志位，防止 setChecked 级联触发 handleCheckChange
					this.isClearing = true;
					const firstLevelNodes = this.treeData.filter(node => node.level === 1);
					firstLevelNodes.forEach(node => {
						this.$refs.tree.setChecked(node, false, true);
					});
					this.$nextTick(() => {
						this.isClearing = false;
					});
				}
			}
		}
	};
</script>

<style lang="less" scoped>
	// 弹出框重置样式
	/deep/ .el-dialog__header {
		border-bottom: 1px solid #e8e8e8;
		margin-right: 0;
		background: #f5f7fa;
	}

	/deep/ .el-dialog__headerbtn .el-dialog__close {
		color: #3a434f;
		font-size: 24px;
	}

	/deep/ .el-dialog__footer {
		border-top: 1px solid #e8e8e8;
		padding: 18px;
	}

	/deep/ .el-dialog__body .el-form-item__label {
		line-height: 26px;
	}

	.dialog-book-json {
		.dialog-content {
			.custom-tree-node {
				flex: 1;
			}

			.tree-container {
				margin-top: 16px;
				max-height: calc(100vh - 500px);
				overflow-y: auto;
				border: 1px solid #e4e7ed;
				border-radius: 4px;
				padding: 12px;
				min-height: 300px;
				display: flex;
				align-items: center;
				justify-content: center;

				.empty-data {
					color: #909399;
					font-size: 14px;
					text-align: center;
				}

				/deep/ .el-tree {
					.el-tree-node {
						.el-tree-node__content {
							height: 36px;

							.tree-node {
								display: flex;
								align-items: center;
								width: 100%;

								.tree-node-label {
									flex: 1;
								}

								.node-tip {
									color: #909399;
									font-size: 12px;
									margin-left: 8px;
								}
							}

							&.is-checked {
								.first-level {
									.tree-node-label {
										color: #409eff;
										font-weight: 500;
									}
								}
							}
						}

						// 子节点样式 - 隐藏所有非第一级节点的checkbox
						.child-node {
							.tree-node-label {
								color: #909399;
							}

							// 隐藏子节点的checkbox
							.el-checkbox {
								// display: none !important;
							}
						}

						// 确保所有非第一级节点都不显示checkbox
						&:not(.first-level) {
							.el-checkbox {
								// display: none !important;
							}
						}

						// 禁用节点的样式
						&.is-disabled {
							.el-checkbox {
								cursor: not-allowed;
								opacity: 0.5;
							}

							.el-tree-node__content {
								cursor: not-allowed;
								color: #c0c4cc;
							}
						}
					}
				}
			}
		}

		.dialog-footer {
			text-align: right;

			.selection-info {
				display: flex;
				justify-content: space-between;
				align-items: center;
				padding: 8px 12px;
				// background-color: #f5f7fa;
				// border-radius: 4px;

				span {
					color: #606266;
					font-size: 14px;
				}
			}
		}
	}
</style>
